import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useSite } from "../lib/SiteContext";
import { useAuth } from "../lib/AuthContext";
import safeNoop from "../lib/safeNoop";
import { CheckCircle2, ArrowRight, Zap, Droplets, Cloud, Cpu, Battery, Sun } from "lucide-react";

const DOMAINS = [
  { id: "energy", label: "Energy", desc: "Smart-meter grid in / out", Icon: Zap, accent: "#F59E0B" },
  { id: "solar", label: "Solar PV", desc: "FusionSolar / inverters", Icon: Sun, accent: "#F59E0B" },
  { id: "water", label: "Water", desc: "Tank, rain, pump", Icon: Droplets, accent: "#3B82F6" },
  { id: "weather", label: "Weather", desc: "Open-Meteo / Wunderground", Icon: Cloud, accent: "#A1A1AA" },
  { id: "device", label: "Devices", desc: "Smart plugs, sensors", Icon: Cpu, accent: "#A1A1AA" },
  { id: "battery", label: "Battery", desc: "Future home battery", Icon: Battery, accent: "#10B981" },
];

const CONNECTORS = [
  { id: "manual", label: "Manual entry", desc: "Enter daily values by hand — best for getting started fast.", recommended: true },
  { id: "csv_import", label: "CSV import", desc: "Drop in exports from your device's companion app — works for every provider." },
  { id: "mobile_local_sync", label: "Mobile local sync", desc: "Your phone reads HomeWizard on home Wi-Fi and pushes a snapshot to the cloud. Planned." },
  { id: "bridge", label: "Always-on local bridge", desc: "A small agent at home (Mac mini, Raspberry Pi, Docker, Home Assistant, n8n) pushes 24/7. Planned." },
  { id: "cloud_api", label: "Cloud API", desc: "Direct integrations like Open-Meteo, FusionSolar and Wunderground — no exports needed." },
];

export default function Onboarding() {
  const nav = useNavigate();
  const { refresh, selectSite } = useSite();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    name: "My Home",
    location_name: "",
    latitude: 50.821,
    longitude: 3.846,
    roof_area_m2: 144,
    runoff_factor: 0.85,
    tank_capacity_liters: 50000,
    tank_dead_volume_liters: 1000,
    liters_per_kwh: 500,
    daily_water_usage_liters: 150,
    timezone: "Europe/Brussels",
    monitor_domains: ["energy", "water", "weather"],
    connector_choice: "manual",
  });

  const toggleDomain = (id) =>
    setForm((f) => ({
      ...f,
      monitor_domains: f.monitor_domains.includes(id)
        ? f.monitor_domains.filter((d) => d !== id)
        : [...f.monitor_domains, id],
    }));

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const finish = async () => {
    setBusy(true); setError(null);
    try {
      const { data: site } = await api.post("/sites", {
        name: form.name,
        location_name: form.location_name || null,
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
        roof_area_m2: Number(form.roof_area_m2),
        runoff_factor: Number(form.runoff_factor),
        tank_capacity_liters: Number(form.tank_capacity_liters),
        tank_dead_volume_liters: Number(form.tank_dead_volume_liters),
        liters_per_kwh: Number(form.liters_per_kwh),
        daily_water_usage_liters: Number(form.daily_water_usage_liters),
        timezone: form.timezone,
        monitor_domains: form.monitor_domains,
      });
      // Create simple data sources per chosen domain
      // Pick the canonical default provider for each domain so new sites
      // start with a sensible, editable connector rather than "manual".
      const DEFAULT_PROVIDER = {
        weather: "open_meteo",
        solar: "fusionsolar",
        energy: "homewizard",
      };
      for (const d of form.monitor_domains) {
        const provider = DEFAULT_PROVIDER[d] || "manual";
        const mode = d === "weather" ? "cloud_api" : form.connector_choice;
        await api.post(`/sites/${site.id}/data-sources`, {
          domain: d, provider, connector_mode: mode,
          status: mode === "manual" ? "connected" : "not_configured",
          confidence_level: mode === "manual" ? "manual" : "unknown",
          config_json: {}, notes: null,
        });
      }
      // Pre-populate weather if requested + cloud API.
      // Best-effort: if Open-Meteo is unavailable, the user can still complete
      // onboarding and trigger a sync from the Weather page later.
      if (form.monitor_domains.includes("weather")) {
        try {
          await api.post(`/sites/${site.id}/weather/sync?days=30`);
        } catch (syncErr) {
          safeNoop("onboarding.weather-sync-skipped", syncErr);
        }
      }
      await refresh();
      selectSite(site.id);
      nav("/", { replace: true });
    } catch (e) {
      // Auth failures are already handled globally — the AuthContext flips
      // to unauthenticated and the router will redirect to /login. We silently
      // bail out rather than show a confusing red box mid-onboarding.
      if (e.isAuthFailure) return;
      setError(e.userMessage || "Could not create site");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background topo-bg">
      <div className="max-w-3xl mx-auto py-12 px-6">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-md bg-foreground text-background grid place-items-center font-heading font-bold text-sm">D</div>
          <span className="font-heading font-semibold">Dreamhouse Flow</span>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-6">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.</h1>
        <p className="text-muted-foreground mt-2 max-w-xl">Let's set up your first home. We'll keep this short — three steps.</p>

        {/* Progress */}
        <div className="flex items-center gap-2 mt-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${step === s ? "text-foreground" : "text-muted-foreground"}`}>
                <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-semibold border ${step >= s ? "bg-foreground text-background border-foreground" : "border-border"}`}>{s}</div>
                <span className="text-sm">{["Site", "Domains", "Connection"][s - 1]}</span>
              </div>
              {s < 3 && <div className={`flex-1 h-px ${step > s ? "bg-foreground" : "bg-border"}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="card-base mt-6 p-6 space-y-5" data-testid="onboarding-card">
          {step === 1 && <SiteStep form={form} update={update} onNext={() => setStep(2)} />}
          {step === 2 && (
            <DomainStep
              form={form} toggleDomain={toggleDomain}
              onBack={() => setStep(1)} onNext={() => setStep(3)}
            />
          )}
          {step === 3 && (
            <ConnectionStep
              form={form} update={update}
              onBack={() => setStep(2)} onFinish={finish} busy={busy} error={error}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Step components -------------------------------------------------------

function SiteStep({ form, update, onNext }) {
  return (
    <div className="space-y-5">
      <h2 className="font-heading text-xl">Where is your home?</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Site name" value={form.name} onChange={(v) => update("name", v)} testid="ob-name" />
        <Field label="Location" value={form.location_name} onChange={(v) => update("location_name", v)} placeholder="Lierde, Belgium" testid="ob-location" />
        <Field label="Latitude" type="number" value={form.latitude} onChange={(v) => update("latitude", v)} step="any" testid="ob-lat" />
        <Field label="Longitude" type="number" value={form.longitude} onChange={(v) => update("longitude", v)} step="any" testid="ob-lon" />
        <Field label="Roof area (m²)" type="number" value={form.roof_area_m2} onChange={(v) => update("roof_area_m2", v)} testid="ob-roof" />
        <Field label="Runoff factor" type="number" value={form.runoff_factor} onChange={(v) => update("runoff_factor", v)} step="0.01" testid="ob-runoff" />
        <Field label="Tank capacity (L)" type="number" value={form.tank_capacity_liters} onChange={(v) => update("tank_capacity_liters", v)} testid="ob-tank" />
        <Field label="Liters per kWh (pump)" type="number" value={form.liters_per_kwh} onChange={(v) => update("liters_per_kwh", v)} testid="ob-lpkwh" />
      </div>
      <div className="flex justify-end pt-2">
        <button onClick={onNext} className="px-4 py-2 rounded-md bg-foreground text-background text-sm font-medium" data-testid="ob-next-1">
          Next <ArrowRight className="w-3.5 h-3.5 inline ml-1.5" />
        </button>
      </div>
    </div>
  );
}

function DomainStep({ form, toggleDomain, onBack, onNext }) {
  return (
    <div className="space-y-5">
      <h2 className="font-heading text-xl">What do you want to monitor?</h2>
      <p className="text-sm text-muted-foreground">Select all that apply. You can always add more later.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {DOMAINS.map((d) => {
          const sel = form.monitor_domains.includes(d.id);
          return (
            <button key={d.id} onClick={() => toggleDomain(d.id)}
              data-testid={`ob-domain-${d.id}`}
              className={`text-left p-3 rounded-lg border transition-colors ${sel ? "border-foreground bg-secondary" : "border-border hover:border-zinc-700"}`}>
              <div className="flex items-center gap-2.5">
                <d.Icon className="w-4 h-4" style={{ color: d.accent }} />
                <div className="font-medium text-sm">{d.label}</div>
                {sel && <CheckCircle2 className="w-3.5 h-3.5 ml-auto text-foreground" />}
              </div>
              <div className="text-xs text-muted-foreground mt-1.5">{d.desc}</div>
            </button>
          );
        })}
      </div>
      <div className="flex justify-between pt-2">
        <button onClick={onBack} className="px-4 py-2 rounded-md text-sm border border-border">Back</button>
        <button onClick={onNext} disabled={form.monitor_domains.length === 0}
          className="px-4 py-2 rounded-md bg-foreground text-background text-sm font-medium disabled:opacity-50"
          data-testid="ob-next-2">
          Next <ArrowRight className="w-3.5 h-3.5 inline ml-1.5" />
        </button>
      </div>
    </div>
  );
}

function ConnectionStep({ form, update, onBack, onFinish, busy, error }) {
  return (
    <div className="space-y-5">
      <h2 className="font-heading text-xl">How will data come in?</h2>
      <p className="text-sm text-muted-foreground">Pick a default. Each data source can be reconfigured per domain later.</p>
      <div className="space-y-2">
        {CONNECTORS.map((c) => (
          <button key={c.id} onClick={() => update("connector_choice", c.id)}
            data-testid={`ob-connector-${c.id}`}
            className={`w-full text-left p-3 rounded-lg border transition-colors flex items-start gap-3 ${form.connector_choice === c.id ? "border-foreground bg-secondary" : "border-border hover:border-zinc-700"}`}>
            <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex-shrink-0 ${form.connector_choice === c.id ? "border-foreground bg-foreground" : "border-zinc-600"}`} />
            <div className="flex-1">
              <div className="font-medium text-sm flex items-center gap-2">
                {c.label}
                {c.recommended && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-foreground text-background">Recommended</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-1">{c.desc}</div>
            </div>
          </button>
        ))}
      </div>
      <div className="text-xs text-muted-foreground border border-border rounded-md p-3 leading-relaxed">
        <span className="text-foreground font-medium">A note on architecture.</span> The hosted web app deliberately
        doesn't reach into your home network. Mobile local sync needs your phone on home Wi-Fi.
        A bridge is optional, but gives you full hands-off automation when you're ready.
      </div>
      {error && <div className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-md p-2.5">{error}</div>}
      <div className="flex justify-between pt-2">
        <button onClick={onBack} className="px-4 py-2 rounded-md text-sm border border-border">Back</button>
        <button onClick={onFinish} disabled={busy} data-testid="ob-finish"
          className="px-4 py-2 rounded-md bg-foreground text-background text-sm font-medium disabled:opacity-50">
          {busy ? "Creating…" : "Create site"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder = "", step, testid }) {
  return (
    <label className="block">
      <span className="label-tiny block mb-1.5">{label}</span>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} step={step}
        data-testid={testid}
        className="w-full px-3 py-2 rounded-md bg-zinc-950 border border-border focus:border-zinc-500 outline-none text-sm"
      />
    </label>
  );
}
